import React, { useState, useEffect } from 'react';
import { Select, Button, Modal, Form, Input, message, Space, Tooltip } from 'antd';
import { PlusOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { statusAPI } from '../../services/api';

const { Option } = Select;

const StatusSelector = ({ value, onChange, disabled = false }) => {
  const [statusSettings, setStatusSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusForm] = Form.useForm();

  useEffect(() => {
    fetchStatusSettings();
  }, []);

  const fetchStatusSettings = async () => {
    try {
      setLoading(true);
      const response = await statusAPI.getStatusSettings();
      setStatusSettings(response.data || []);
    } catch (error) {
      console.error('Error fetching status settings:', error);
      message.error('Failed to load status settings');
    } finally {
      setLoading(false);
    }
  };

  const openStatusModal = () => {
    statusForm.resetFields();
    setIsStatusModalOpen(true);
  };

  const handleStatusCreate = async (values) => {
    try {
      setLoading(true);
      
      const statusData = {
        key: values.key,
        label: values.label,
        color: values.color || '#1890ff'
      };

      const response = await statusAPI.createStatusSetting(statusData);
      message.success('Status created successfully');
      
      setIsStatusModalOpen(false);
      statusForm.resetFields();
      
      // Refresh the list
      await fetchStatusSettings();
      
      // Auto-select the newly created status
      if (onChange) {
        onChange(response.data.key);
      }
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to create status');
      console.error('Error creating status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (color) => {
    // Ensure we have a valid color, default to blue if not
    return color && color.startsWith('#') ? color : '#1890ff';
  };

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          style={{ flex: 1 }}
          placeholder="Select task status"
          value={value}
          onChange={onChange}
          disabled={disabled}
          loading={loading}
          allowClear
        >
          {statusSettings.map(status => (
            <Option key={status.key} value={status.key}>
              <Space>
                <div 
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(status.color),
                    display: 'inline-block'
                  }}
                />
                <span>{status.label}</span>
              </Space>
            </Option>
          ))}
        </Select>
        <Tooltip title="Create new status">
          <Button
            icon={<PlusOutlined />}
            onClick={openStatusModal}
            disabled={disabled}
          />
        </Tooltip>
      </Space.Compact>

      {/* Quick Status Creation Modal */}
      <Modal
        title="Create Status"
        open={isStatusModalOpen}
        onCancel={() => setIsStatusModalOpen(false)}
        footer={null}
        width={500}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusCreate}
        >
          <Form.Item
            name="key"
            label="Status Key"
            rules={[
              { required: true, message: 'Please enter a status key' },
              { pattern: /^[a-z_]+$/, message: 'Key must be lowercase letters and underscores only' },
              { min: 2, message: 'Key must be at least 2 characters' }
            ]}
          >
            <Input placeholder="e.g., in_progress, completed, on_hold" />
          </Form.Item>

          <Form.Item
            name="label"
            label="Display Label"
            rules={[
              { required: true, message: 'Please enter a display label' },
              { min: 2, message: 'Label must be at least 2 characters' }
            ]}
          >
            <Input placeholder="e.g., In Progress, Completed, On Hold" />
          </Form.Item>

          <Form.Item
            name="color"
            label="Color"
            rules={[
              { pattern: /^#[0-9A-Fa-f]{6}$/, message: 'Please enter a valid hex color (e.g., #1890ff)' }
            ]}
          >
            <Input 
              placeholder="#1890ff" 
              addonBefore={
                <div 
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: statusForm.getFieldValue('color') || '#1890ff',
                    borderRadius: 2,
                    border: '1px solid #d9d9d9'
                  }}
                />
              }
            />
          </Form.Item>

          <div style={{ 
            padding: 12, 
            backgroundColor: '#f0f8f0', 
            borderRadius: 4, 
            marginBottom: 16,
            fontSize: 12,
            color: '#52c41a'
          }}>
            <CheckCircleOutlined style={{ marginRight: 4 }} />
            <strong>Note:</strong> The status key should be unique and will be used internally. 
            The display label is what users will see.
          </div>

          <div style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
            <Button onClick={() => setIsStatusModalOpen(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Status
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default StatusSelector;
